import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import CombinationsSelectorProgressBar from '@/modules/ConfiguratorCombinations/components/CombinationsSelectorProgressBar.vue';
import CombinationsConfiguratorTemplate from '@/modules/ConfiguratorCombinations/components/CombinationsConfiguratorTemplate.vue';
import CombinationsProductsControls from '@/modules/ConfiguratorCombinations/components/CombinationsProductsControls.vue';
import CombinationsProductTable from '@/modules/ConfiguratorCombinations/components/CombinationsProductTable.vue';
import CombinationsProductDropdowns from '@/modules/ConfiguratorCombinations/components/CombinationsProductDropdowns.vue';
import CombinationsTotalWidth from '@/modules/ConfiguratorCombinations/components/CombinationsTotalWidth.vue';
import CombinationsWarning from '@/modules/ConfiguratorCombinations/components/CombinationsWarning.vue';
import CombinationsStepInfo from '@/modules/ConfiguratorCombinations/components/CombinationsStepInfo.vue';
import CombinationsSeparatedBoxes from '@/modules/ConfiguratorCombinations/components/CombinationsSeparatedBoxes.vue';
import GuideTypeSelectorModal from '@/modules/ConfiguratorCombinations/components/GuideTypeSelectorModal.vue';
import {
    areWindowsOfSameHeight,
    computeAdjacentLeftGuideValue,
    computeGuideValueForSegment,
    computeLargestRollerShutterBoxSize,
    computeMaxRollerShutterBoxAndSelectedBox,
    getCombinedWidth,
    getPositionDropdownOptions,
    getThreeWindowCombinationType,
    getTwoWindowCombinationType,
    getWidthDifference,
    getWindowDriveValue as getWindowDriveValueFromRows,
    isCombinationInvalid,
    sortSelectedIdsByPosition,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import { useCombinationsConfig } from '@/modules/ConfiguratorCombinations/composables/useCombinationsConfig';
import { useCombinationsStepNavigation } from '@/modules/ConfiguratorCombinations/composables/useCombinationsStepNavigation';
import {
    GuideImageClickPayload,
    GuideType,
    useGuideImages,
} from '@/modules/ConfiguratorCombinations/composables/useGuideImages';
import {
    combinationsConfiguratorSteps,
    SEPARATED_BOXES_STEP_INDEX,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';


export default /*#__PURE__*/_defineComponent({
  __name: 'CombinationsConfiguratorSelectorPopup',
  setup(__props) {

// ============================================================================
// Imports
// ============================================================================
const { proxy } = getCurrentInstance()!;

// ============================================================================
// Store Accessors
// ============================================================================
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null => {
    return proxy!.$store.getters['configurator/activeProductForm'](productFormId);
};

const updateActiveProductFormValue = (payload: UpdateProductFormEntry) => {
    proxy!.$store.dispatch('configurator/updateActiveProductFormValue', payload);
};

// ============================================================================
// Core State
// ============================================================================
// Popup / product context
const visible = ref<boolean>(false);
const productData = ref<ProductRowItem[]>([]);
const productName = ref<string>('');

// Form data backing the table & dropdowns
const productRowsFormData = ref<CombinationsConfiguratorFormData[]>([]);
const productsSelectedForCombination = ref<number[]>([]);

// Width step state
const totalWidth = ref<number>(0);

// Step 3 — separate boxes toggle (only shown when currentConfig.showSeparatedWindowsOptions is true)
const useSeparateBoxes = ref<boolean>(false);

// Last-step (guides ) state
const guideType = ref<string>('');
const secondGuideType = ref<string>('');

const combinationsConfiguratorTemplateRef = ref<any>(null);

// ============================================================================
// Configuration & Header Fields
// (product config lookup, PIDs, table/dropdown field definitions)
// ============================================================================
const {
    tableHeaderFields,
    FilteredCombinationsOptions,
    currentConfig,
    checklistFieldConfig,
    activeSteps,
    shouldAddCombinationInMultiposition,
    shouldShowAssemblyCode,
    showCombinationType,
    // isHeroProduct is currently unused in the template/script
    canvasTemplateConfiguration,
    noChoiceGuideValue,
    getGuideTypeChecklistValue,
    configuratorType,
    setCorrectPIdsForFormData,
    resetConfig,
} = useCombinationsConfig(productName, {
    productRowsFormData,
    selectedProductFormIds: productsSelectedForCombination,
});

const sortedProductsSelectedForCombination = computed(() => {
    const positionPID = FilteredCombinationsOptions.value.ASSEMBLY_POSITION_PID.toString();

    return sortSelectedIdsByPosition(productRowsFormData.value, productsSelectedForCombination.value, positionPID);
});

// ============================================================================
// Last Step — Guide State, Combination Type & Validation
// (drives the dropdowns shown on the final configurator step)
// ============================================================================
const {
    isFirstGuideSingle,
    isSecondGuideSingle,
    areBothGuidesSingle,
    isFirstGuidePartition,
    isSecondGuidePartition,
    areBothGuidesPartition,
    individualGuideTypes,
    guideTypeOptions,
    setIndividualGuideType,
} = useGuideImages({
    guideType: () => guideType.value,
    secondGuideType: () => secondGuideType.value,
});

const activeGuideSelection = ref<GuideImageClickPayload | null>(null);

const activeGuideType = computed<GuideType | null>(() => {
    const selection = activeGuideSelection.value;

    return selection ? individualGuideTypes.value[selection.windowIndex][selection.corner] : null;
});

const getWindowDriveValue = (productFormId: number): string => {
    return getWindowDriveValueFromRows(
        productRowsFormData.value,
        productFormId,
        FilteredCombinationsOptions.value.DRIVE_PID.toString(),
    );
};

const isSinglePartitionCombination = computed(() => isFirstGuideSingle.value && isSecondGuidePartition.value);
const isPartitionSingleCombination = computed(() => isFirstGuidePartition.value && isSecondGuideSingle.value);

const combinationType = computed(() => {
    const driveOne = getWindowDriveValue(sortedProductsSelectedForCombination.value[0]);
    const driveTwo = getWindowDriveValue(sortedProductsSelectedForCombination.value[1]);
    const driveThree = getWindowDriveValue(sortedProductsSelectedForCombination.value[2]);

    if (productsSelectedForCombination.value.length === 2) {
        return getTwoWindowCombinationType(driveOne, driveTwo, isFirstGuideSingle.value);
    }

    if (productsSelectedForCombination.value.length === 3) {
        return getThreeWindowCombinationType(driveOne, driveTwo, driveThree, {
            areBothGuidesSingle: areBothGuidesSingle.value,
            isSinglePartitionCombination: isSinglePartitionCombination.value,
            isPartitionSingleCombination: isPartitionSingleCombination.value,
            areBothGuidesPartition: areBothGuidesPartition.value,
        });
    }

    return '';
});

const isInvalidCombination = computed(() =>
    isCombinationInvalid({
        isFirstGuideSingle: isFirstGuideSingle.value,
        isSecondGuideSingle: isSecondGuideSingle.value,
        isFirstGuidePartition: isFirstGuidePartition.value,
        isSecondGuidePartition: isSecondGuidePartition.value,
        selectedCount: productsSelectedForCombination.value.length,
        firstWindowDrive: getWindowDriveValue(sortedProductsSelectedForCombination.value[0]),
        secondWindowDrive: getWindowDriveValue(sortedProductsSelectedForCombination.value[1]),
        thirdWindowDrive: getWindowDriveValue(sortedProductsSelectedForCombination.value[2]),
    }),
);

const combinationsWarningMessage = computed<any>(() => {
    if (productsSelectedForCombination.value.length === 0) {
        return proxy!.$t('No positions selected! Please select positions from the list.');
    }

    if (isInvalidCombination.value) {
        return proxy!.$t(
            'When selecting a partition guide, both drives cannot be next to it. If you want to have both drives in the middle, please select a single guide.',
        );
    }

    return '';
});

const activeCombinationsWarningMessage = computed<any>(() => {
    const allowedCombinedWidth = currentConfig.value?.maximumAssemblySize || Infinity;

    if (totalWidth.value >= allowedCombinedWidth) {
        return proxy!.$t(
            'Please note! A box longer than 300 cm will be made in two parts and will have a joint in the middle.',
        );
    }

    return '';
});

// ============================================================================
// Width — Total & Combined Width
// ============================================================================
const combinedWidth = computed(() =>
    getCombinedWidth({
        rows: productRowsFormData.value,
        selectedIds: productsSelectedForCombination.value,
        widthPID: FilteredCombinationsOptions.value.WIDTH_PID.toString(),
        finalWidthPID: FilteredCombinationsOptions.value.FINAL_WIDTH_PID.toString(),
        dimensionsTypePID: FilteredCombinationsOptions.value.DIMENSIONS_TYPE_PID.toString(),
    }),
);

const widthDifference = computed(() => getWidthDifference(totalWidth.value, combinedWidth.value));

// ============================================================================
// Product Form Data — Derived Collections
// ============================================================================
const productFormValues = computed(() =>
    productData.value.map((product) => ({
        activeProductFormId: product.activeProductFormId,
        activeProductFormValues: getProductFormValues(product.activeProductFormId),
    })),
);

const filteredProductRowsFormData = computed<CombinationsConfiguratorFormData[]>(() =>
    productRowsFormData.value
        .filter((row) => sortedProductsSelectedForCombination.value.includes(row.activeProductFormId))
        .sort((a, b) => {
            return (
                sortedProductsSelectedForCombination.value.indexOf(a.activeProductFormId) -
                sortedProductsSelectedForCombination.value.indexOf(b.activeProductFormId)
            );
        }),
);

// ============================================================================
// Table & Dropdown Field Filters
// ============================================================================
const getTableFields = computed(() =>
    tableHeaderFields.value.filter((field) => field.segment === 'table' && field.active),
);

const getDropdownTypeFields = computed(() =>
    tableHeaderFields.value.filter((field) => field.segment === 'configurator' && field.active),
);

const dropdownChecklistFields = computed<ChecklistField[]>(() => {
    const guideOptionToFilter = [noChoiceGuideValue.value];
    const driveOptionToFilter = 'Nedefinirano';

    // We need to get vodila and pogon dropdown options
    if (!productData.value.length) {
        return [];
    }

    const combinationsChecklists = productData.value[0].editableFields.filter(
        (field) =>
            field.pId === FilteredCombinationsOptions.value.LEFT_GUIDE_PID ||
            field.pId === FilteredCombinationsOptions.value.RIGHT_GUIDE_PID ||
            field.pId === FilteredCombinationsOptions.value.DRIVE_PID,
    );

    return combinationsChecklists.map((checklist) => {
        if (!checklist.checklistDropdownField) {
            return checklist;
        }

        switch (checklist.pId) {
            case FilteredCombinationsOptions.value.LEFT_GUIDE_PID:
            case FilteredCombinationsOptions.value.RIGHT_GUIDE_PID:
                return {
                    ...checklist,
                    checklistDropdownField: {
                        ...checklist.checklistDropdownField,
                        checklistDropdownOptions: checklist.checklistDropdownField.checklistDropdownOptions.filter(
                            (option) => !guideOptionToFilter.includes(option.vId),
                        ),
                    },
                };
            case FilteredCombinationsOptions.value.DRIVE_PID:
                return {
                    ...checklist,
                    checklistDropdownField: {
                        ...checklist.checklistDropdownField,
                        checklistDropdownOptions: checklist.checklistDropdownField.checklistDropdownOptions.filter(
                            (option) => option.vId !== driveOptionToFilter,
                        ),
                    },
                };
            default:
                return checklist;
        }
    });
});

const areFirstTwoWindowsOfSameHeight = computed(() => {
    const validNumberOfWindows = [2, 3];

    if (!validNumberOfWindows.includes(productsSelectedForCombination.value.length)) {
        return false;
    }

    return areWindowsOfSameHeight(
        productRowsFormData.value,
        sortedProductsSelectedForCombination.value[0],
        sortedProductsSelectedForCombination.value[1],
        FilteredCombinationsOptions.value.HEIGHT_PID.toString(),
    );
});

const areLastTwoWindowsOfSameHeight = computed(() => {
    if (productsSelectedForCombination.value.length !== 3) {
        return false;
    }

    return areWindowsOfSameHeight(
        productRowsFormData.value,
        sortedProductsSelectedForCombination.value[1],
        sortedProductsSelectedForCombination.value[2],
        FilteredCombinationsOptions.value.HEIGHT_PID.toString(),
    );
});

function getGuideOptionsWithMostValues() {
    const leftGuideOptions =
        dropdownChecklistFields.value.find(
            (checklist) => checklist.pId === FilteredCombinationsOptions.value.LEFT_GUIDE_PID,
        )?.checklistDropdownField?.checklistDropdownOptions || [];

    const rightGuideOptions =
        dropdownChecklistFields.value.find(
            (checklist) => checklist.pId === FilteredCombinationsOptions.value.RIGHT_GUIDE_PID,
        )?.checklistDropdownField?.checklistDropdownOptions || [];

    return leftGuideOptions.length >= rightGuideOptions.length ? leftGuideOptions : rightGuideOptions;
}

const pogonOptions = computed(() =>
    getGuideOptionsWithMostValues().filter((option) => {
        if (!areFirstTwoWindowsOfSameHeight.value && option.vId === 'Pregradno') {
            return false;
        }

        return true;
    }),
);

const secondPogonOptions = computed(() =>
    getGuideOptionsWithMostValues().filter((option) => {
        if (!areLastTwoWindowsOfSameHeight.value && option.vId === 'Pregradno') {
            return false;
        }

        return true;
    }),
);

// ============================================================================
// Selection Handling — Toggle products & adjust dropdown options
// ============================================================================
function adjustOptionsForDropdowns() {
    // Update position options based on the number of selected products
    const positionPID = FilteredCombinationsOptions.value.ASSEMBLY_POSITION_PID.toString();
    const options = getPositionDropdownOptions(productsSelectedForCombination.value.length);

    // Update position options for all rows
    productRowsFormData.value.forEach((row) => {
        const positionField = row.formData.find((field) => field.fieldPID === positionPID);
        if (positionField) {
            positionField.options = options;
        }
    });
}

function toggleProductSelection(productId: number) {
    const index = productsSelectedForCombination.value.indexOf(productId);

    if (index === -1) {
        if (productsSelectedForCombination.value.length >= 3) {
            const firstSelectedElement = productsSelectedForCombination.value[0];
            const firstSelectedElementIndex = productsSelectedForCombination.value.findIndex(
                (row) => row === firstSelectedElement,
            );

            productsSelectedForCombination.value.splice(firstSelectedElementIndex, 1);
        }

        productsSelectedForCombination.value.push(productId);
    } else {
        productsSelectedForCombination.value.splice(index, 1);
    }

    totalWidth.value = 0;
    adjustOptionsForDropdowns();
}

// ============================================================================
// Step Navigation
// ============================================================================
const {
    currentStep,
    displayNextArrow,
    displayPrevArrow,
    isLastStep,
    currentStepDescription,
    stepCompletions,
    isStepCompletedByIndex,
    generateStepsAccessibilityArray,
    goToStep,
    goToNextStep,
    goToPrevStep,
    updateArrowVisibility,
    resetNavigation,
} = useCombinationsStepNavigation({
    activeSteps,
    FilteredCombinationsOptions,
    productRowsFormData,
    productsSelectedForCombination,
    filteredProductRowsFormData,
    combinedWidth,
    totalWidth,
    guideType,
    secondGuideType,
});

// Description shown in the single step-info box below the form fields.
// On step 3, when the active config exposes the separate-boxes option, show the
// help text for that toggle; otherwise fall back to the active step description.
const stepInfoDescription = computed(() => {
    if (currentStep.value === 3 && currentConfig.value?.showSeparatedWindowsOptions) {
        const separatedBoxesStep = combinationsConfiguratorSteps[SEPARATED_BOXES_STEP_INDEX];
        return separatedBoxesStep
            ? (proxy!.$t(separatedBoxesStep.description) as string)
            : currentStepDescription.value;
    }
    return currentStepDescription.value;
});

// ============================================================================
// Computed Guide Values per Window
// (resolves the inner/outer guide value each window receives)
// ============================================================================
const firstGuideComputedValue = computed(() =>
    computeGuideValueForSegment(
        productsSelectedForCombination.value.length,
        2,
        areFirstTwoWindowsOfSameHeight.value,
        guideType.value,
    ),
);

const secondGuideComputedValue = computed(() =>
    computeGuideValueForSegment(
        productsSelectedForCombination.value.length,
        3,
        areLastTwoWindowsOfSameHeight.value,
        secondGuideType.value,
    ),
);

const firstWindowRightGuideValue = computed(() => firstGuideComputedValue.value);

const secondWindowLeftGuideValue = computed(() =>
    computeAdjacentLeftGuideValue(firstWindowRightGuideValue.value, noChoiceGuideValue.value),
);

const secondWindowRightGuideValue = computed(() => secondGuideComputedValue.value);

const thirdWindowLeftGuideValue = computed(() =>
    computeAdjacentLeftGuideValue(secondWindowRightGuideValue.value, noChoiceGuideValue.value),
);

// ============================================================================
// Form Data Initialization & Updates
// (builds product rows from store, mutates field values in-place)
// ============================================================================
function renderFormData() {
    productRowsFormData.value = productFormValues.value.map((activeProductForm) => {
        const formValues = activeProductForm.activeProductFormValues;
        const combinationsProductRow: any = {
            activeProductFormId: activeProductForm.activeProductFormId,
            formData: [],
            isHighlighted: false,
        };

        let isProductAlreadyInCombination = false;

        tableHeaderFields.value.forEach(
            (field: {
                pid: any;
                label: string;
                shouldUpdateConfigurator: boolean;
                type: string;
                isReadOnly: boolean;
                segment: string;
                options?: any[];
                step?: number;
                value?: string | number | boolean;
                isNarrow?: boolean;
            }) => {
                const formValue = formValues?.[field.pid];
                const defaultValue = field.value || formValue || '';
                const formattedValue = field.isReadOnly ? defaultValue : '';

                const checklistFieldInformation = {
                    fieldPID: field.pid,
                    fieldLabel: field.label,
                    fieldValue: formattedValue === 'Nedefinirano' ? '' : formattedValue,
                    shouldUpdateConfigurator: field.shouldUpdateConfigurator,
                    fieldType: field.type,
                    isReadOnly: field.isReadOnly || false,
                    segment: field.segment || 'table',
                    options: field?.options || [],
                    step: field?.step || 1,
                    isNarrow: field?.isNarrow || false,
                };

                if (field.pid === FilteredCombinationsOptions.value.COMBINATIONS_PID && !!formValue) {
                    isProductAlreadyInCombination = true;
                }

                // Highlight the product row if it is already part of a combination
                combinationsProductRow.isHighlighted = isProductAlreadyInCombination;

                combinationsProductRow.formData.push(checklistFieldInformation);
            },
        );

        return combinationsProductRow;
    });
}

function onFormFieldChange({
    productId,
    fieldPID,
    value,
}: {
    productId: number;
    fieldPID: string;
    value: string | number | boolean;
}) {
    const rowIndex = productRowsFormData.value.findIndex((row) => row.activeProductFormId === productId);
    if (rowIndex !== -1) {
        const fieldIndex = productRowsFormData.value[rowIndex].formData.findIndex(
            (field: { fieldPID: string }) => field.fieldPID === fieldPID,
        );
        if (fieldIndex !== -1) {
            productRowsFormData.value[rowIndex].formData[fieldIndex].fieldValue = value as string | number;
        }
    }
}

// ============================================================================
// Popup Lifecycle (open / reset / close)
// ============================================================================
async function openPopup(data: { rows: ProductRowItem[]; productName: string }) {
    visible.value = true;
    productData.value = data.rows;
    productName.value = data.productName;

    setCorrectPIdsForFormData();
    await nextTick();
    renderFormData();
    updateArrowVisibility();
    await nextTick();
}

function resetForm() {
    productData.value = [];
    productsSelectedForCombination.value = [];
    productRowsFormData.value = [];
    guideType.value = '';
    secondGuideType.value = '';
    activeGuideSelection.value = null;
    totalWidth.value = 0;
    useSeparateBoxes.value = false;
    resetConfig();
    resetNavigation();
}

function closePopup() {
    resetForm();
    visible.value = false;
}

// ============================================================================
// Multiposition — Format & Submit Combination
// (writes derived field values back to store before emitting the event)
// ============================================================================
function formatTotalWidthFields({ value }: { value: number }) {
    const totalWidthPID = FilteredCombinationsOptions.value.TOTAL_WIDTH_PID.toString();

    const firstProductId = sortedProductsSelectedForCombination.value[0];

    onFormFieldChange({
        productId: firstProductId,
        fieldPID: totalWidthPID,
        value,
    });
}

function formatGuideFields({
    firstWindowId,
    secondWindowId,
    thirdWindowId,
}: {
    firstWindowId: number;
    secondWindowId: number;
    thirdWindowId?: number;
}) {
    const leftGuidePID = FilteredCombinationsOptions.value.LEFT_GUIDE_PID.toString();
    const rightGuidePID = FilteredCombinationsOptions.value.RIGHT_GUIDE_PID.toString();

    // Handle outer guides (always 'Enojno')
    // First window's left guide
    onFormFieldChange({
        productId: firstWindowId,
        fieldPID: leftGuidePID,
        value: 'Enojno',
    });

    // Last window's right guide
    onFormFieldChange({
        productId: thirdWindowId ?? secondWindowId,
        fieldPID: rightGuidePID,
        value: 'Enojno',
    });

    // Handle inner guides based on selected guide type

    // First window's right guide
    onFormFieldChange({
        productId: firstWindowId,
        fieldPID: rightGuidePID,
        value: firstWindowRightGuideValue.value,
    });

    // Second window's left guide
    onFormFieldChange({
        productId: secondWindowId,
        fieldPID: leftGuidePID,
        value: secondWindowLeftGuideValue.value,
    });

    if (thirdWindowId) {
        // Second window's right guide
        onFormFieldChange({
            productId: secondWindowId,
            fieldPID: rightGuidePID,
            value: secondWindowRightGuideValue.value,
        });

        // Third window's left guide
        onFormFieldChange({
            productId: thirdWindowId,
            fieldPID: leftGuidePID,
            value: thirdWindowLeftGuideValue.value,
        });
    }
}

function formatGuideFieldsWithGuideTypes({
    firstWindowId,
    secondWindowId,
    thirdWindowId,
}: {
    firstWindowId: number;
    secondWindowId: number;
    thirdWindowId?: number;
}) {
    const guideTypePID = FilteredCombinationsOptions.value.HERO_GUIDE_TYPE_PID.toString();
    const leftGuideTypePID = FilteredCombinationsOptions.value.HERO_LEFT_GUIDE_TYPE_PID.toString();
    const rightGuideTypePID = FilteredCombinationsOptions.value.HERO_RIGHT_GUIDE_TYPE_PID.toString();

    const supportsGuideTypes = productRowsFormData.value
        .find((row) => row.activeProductFormId === firstWindowId)
        ?.formData.some((field) => field.fieldPID === guideTypePID);

    if (!supportsGuideTypes) {
        return;
    }

    const values: Record<number, { leftCorner: string; rightCorner: string }> = {
        [firstWindowId]: {
            leftCorner: 'Enojno',
            rightCorner: firstWindowRightGuideValue.value,
        },
        [secondWindowId]: {
            leftCorner: secondWindowLeftGuideValue.value,
            rightCorner: thirdWindowId ? secondWindowRightGuideValue.value : 'Enojno',
        },
    };

    if (thirdWindowId) {
        values[thirdWindowId] = {
            leftCorner: thirdWindowLeftGuideValue.value,
            rightCorner: 'Enojno',
        };
    }

    const mappedValues: Record<number, { leftCorner: string; rightCorner: string }> = {
        [firstWindowId]: {
            leftCorner: getGuideTypeChecklistValue(individualGuideTypes.value[1].leftCorner),
            rightCorner:
                values[firstWindowId].rightCorner === 'Enojno'
                    ? getGuideTypeChecklistValue(individualGuideTypes.value[1].rightCorner)
                    : '',
        },
        [secondWindowId]: {
            leftCorner:
                values[secondWindowId].leftCorner === 'Enojno'
                    ? getGuideTypeChecklistValue(individualGuideTypes.value[2].leftCorner)
                    : '',
            rightCorner:
                values[secondWindowId].rightCorner === 'Enojno'
                    ? getGuideTypeChecklistValue(individualGuideTypes.value[2].rightCorner)
                    : '',
        },
    };

    if (thirdWindowId) {
        mappedValues[thirdWindowId] = {
            leftCorner:
                values[thirdWindowId].leftCorner === 'Enojno'
                    ? getGuideTypeChecklistValue(individualGuideTypes.value[3].leftCorner)
                    : '',
            rightCorner: getGuideTypeChecklistValue(individualGuideTypes.value[3].rightCorner),
        };
    }

    const activeGuideType = mappedValues[firstWindowId].leftCorner;
    const firstWindowGuideTypesAreSame =
        values[firstWindowId].rightCorner !== 'Enojno' || mappedValues[firstWindowId].rightCorner === activeGuideType;
    const secondWindowGuideTypesAreSame =
        (values[secondWindowId].leftCorner !== 'Enojno' ||
            mappedValues[secondWindowId].leftCorner === activeGuideType) &&
        (values[secondWindowId].rightCorner !== 'Enojno' ||
            mappedValues[secondWindowId].rightCorner === activeGuideType);
    const thirdWindowGuideTypesAreSame =
        !thirdWindowId ||
        ((values[thirdWindowId].leftCorner !== 'Enojno' ||
            mappedValues[thirdWindowId].leftCorner === activeGuideType) &&
            mappedValues[thirdWindowId].rightCorner === activeGuideType);
    const allGuideTypesAreSame =
        firstWindowGuideTypesAreSame && secondWindowGuideTypesAreSame && thirdWindowGuideTypesAreSame;

    if (allGuideTypesAreSame) {
        onFormFieldChange({
            productId: firstWindowId,
            fieldPID: guideTypePID,
            value: activeGuideType,
        });
        onFormFieldChange({
            productId: secondWindowId,
            fieldPID: guideTypePID,
            value: activeGuideType,
        });

        setShouldUpdateConfigurator(firstWindowId, leftGuideTypePID, false);
        setShouldUpdateConfigurator(firstWindowId, rightGuideTypePID, false);
        setShouldUpdateConfigurator(secondWindowId, leftGuideTypePID, false);
        setShouldUpdateConfigurator(secondWindowId, rightGuideTypePID, false);

        if (thirdWindowId) {
            onFormFieldChange({
                productId: thirdWindowId,
                fieldPID: guideTypePID,
                value: activeGuideType,
            });
            setShouldUpdateConfigurator(thirdWindowId, leftGuideTypePID, false);
            setShouldUpdateConfigurator(thirdWindowId, rightGuideTypePID, false);
        }

        return;
    }

    onFormFieldChange({
        productId: firstWindowId,
        fieldPID: guideTypePID,
        value: 'Različni vodili',
    });
    onFormFieldChange({
        productId: firstWindowId,
        fieldPID: leftGuideTypePID,
        value: mappedValues[firstWindowId].leftCorner,
    });
    onFormFieldChange({
        productId: firstWindowId,
        fieldPID: rightGuideTypePID,
        value: mappedValues[firstWindowId].rightCorner,
    });

    onFormFieldChange({
        productId: secondWindowId,
        fieldPID: guideTypePID,
        value: 'Različni vodili',
    });
    onFormFieldChange({
        productId: secondWindowId,
        fieldPID: leftGuideTypePID,
        value: mappedValues[secondWindowId].leftCorner,
    });
    onFormFieldChange({
        productId: secondWindowId,
        fieldPID: rightGuideTypePID,
        value: mappedValues[secondWindowId].rightCorner,
    });

    setShouldUpdateConfigurator(firstWindowId, leftGuideTypePID, true);
    setShouldUpdateConfigurator(firstWindowId, rightGuideTypePID, true);
    setShouldUpdateConfigurator(secondWindowId, leftGuideTypePID, true);
    setShouldUpdateConfigurator(secondWindowId, rightGuideTypePID, true);

    if (thirdWindowId) {
        onFormFieldChange({
            productId: thirdWindowId,
            fieldPID: guideTypePID,
            value: 'Različni vodili',
        });
        onFormFieldChange({
            productId: thirdWindowId,
            fieldPID: leftGuideTypePID,
            value: mappedValues[thirdWindowId].leftCorner,
        });
        onFormFieldChange({
            productId: thirdWindowId,
            fieldPID: rightGuideTypePID,
            value: mappedValues[thirdWindowId].rightCorner,
        });

        setShouldUpdateConfigurator(thirdWindowId, leftGuideTypePID, true);
        setShouldUpdateConfigurator(thirdWindowId, rightGuideTypePID, true);
    }
}

function setShouldUpdateConfigurator(productId: number, fieldPID: string, shouldUpdateConfigurator: boolean): void {
    const productRow = productRowsFormData.value.find((row) => row.activeProductFormId === productId);
    const formField = productRow?.formData.find((field) => field.fieldPID === fieldPID);

    if (formField) {
        formField.shouldUpdateConfigurator = shouldUpdateConfigurator;
    }
}

function formatGuidesForHeroProducts() {
    // Process only first two or three windows
    const windowsToProcess = filteredProductRowsFormData.value.slice(0, filteredProductRowsFormData.value.length);

    if (![2, 3].includes(windowsToProcess.length)) {
        return;
    }

    const [firstWindow, secondWindow, thirdWindow] = windowsToProcess;

    const firstWindowId = firstWindow.activeProductFormId;
    const secondWindowId = secondWindow.activeProductFormId;
    const thirdWindowId = thirdWindow?.activeProductFormId;

    const productFormIds = {
        firstWindowId,
        secondWindowId,
        thirdWindowId,
    };

    formatGuideFields(productFormIds);
    formatGuideFieldsWithGuideTypes(productFormIds);
}

function formatRollerShutterBoxSizeBasedOnSelectedSize() {
    // We need to set roller shutter box sizes for all products THE SAME ( if both are already automatic, we leave them as is )
    // If one is automatic and the other is not, we compare selected box size for the non-automatic and set both to that size
    const rollerShutterBoxSizePID = FilteredCombinationsOptions.value.ROLLER_SHUTTER_BOX_SIZE_PID;
    const selectedRollerShutterBoxSizePID = FilteredCombinationsOptions.value.SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID;
    const boxTypePID = FilteredCombinationsOptions.value.BOX_TYPE_PID;

    const { boxSize, boxType } = computeLargestRollerShutterBoxSize(
        productRowsFormData.value,
        productsSelectedForCombination.value,
        rollerShutterBoxSizePID,
        selectedRollerShutterBoxSizePID,
        boxTypePID ?? null,
    );

    if (boxSize === null && boxType === null) {
        return;
    }

    // Set all products to the target box size (only update the box size field, not the selected box size)
    productsSelectedForCombination.value.forEach((productId) => {
        if (boxSize !== null) {
            onFormFieldChange({
                productId,
                fieldPID: rollerShutterBoxSizePID,
                value: String(boxSize),
            });
        }

        if (boxType !== null) {
            onFormFieldChange({
                productId,
                fieldPID: boxTypePID,
                value: String(boxType),
            });
        }
    });
}

function formatRollerShutterBoxSizeBasedOnDepthAndHeight() {
    // BOX SIZE IS BOX DEPTH AND SELECTED BOX SIZE IS BOX HEIGHT IN THIS CASE
    // Uses computeMaxRollerShutterBoxAndSelectedBox, which treats the two fields independently — it collects all ROLLER_SHUTTER_BOX_SIZE_PID values separately and all SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID values separately, then takes the max of each.

    // Writes: both ROLLER_SHUTTER_BOX_SIZE_PID and SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID for all selected products.
    // Never bails — each field is updated independently if its max is not null.
    const rollerShutterBoxSizePID = FilteredCombinationsOptions.value.ROLLER_SHUTTER_BOX_SIZE_PID.toString();
    const selectedRollerShutterBoxSizePID =
        FilteredCombinationsOptions.value.SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID.toString();

    // THERE IS NO AUTOMATIC VALUE, SO WE COMPARE ALL VALUES AND SET MAX TO ALL PRODUCTS
    const { maxBoxSize, maxSelectedBoxSize } = computeMaxRollerShutterBoxAndSelectedBox(
        productRowsFormData.value,
        productsSelectedForCombination.value,
        rollerShutterBoxSizePID,
        selectedRollerShutterBoxSizePID,
    );

    // Apply largest values to all products
    productsSelectedForCombination.value.forEach((productId) => {
        if (maxBoxSize !== null) {
            onFormFieldChange({
                productId,
                fieldPID: rollerShutterBoxSizePID,
                value: String(maxBoxSize),
            });
        }
        if (maxSelectedBoxSize !== null) {
            onFormFieldChange({
                productId,
                fieldPID: selectedRollerShutterBoxSizePID,
                value: String(maxSelectedBoxSize),
            });
        }
    });
}

function formatRollerShutterBoxSizeField() {
    switch (currentConfig.value?.normalizeCombinationsSizeType) {
        case 'selectedSize':
            formatRollerShutterBoxSizeBasedOnSelectedSize();
            break;
        case 'depthAndHeight':
            formatRollerShutterBoxSizeBasedOnDepthAndHeight();
            break;

        default:
            break;
    }
}

function formatCombinedSystemBoxField() {
    if (!currentConfig.value?.showSeparatedWindowsOptions) {
        return;
    }

    const combinedAssemblyPID = FilteredCombinationsOptions.value.COMBINED_ASSEMBLY_PID.toString();
    const totalWidthPID = FilteredCombinationsOptions.value.TOTAL_WIDTH_PID.toString();

    productsSelectedForCombination.value.forEach((productId) => {
        onFormFieldChange({
            productId,
            fieldPID: combinedAssemblyPID,
            value: useSeparateBoxes.value,
        });

        if (!useSeparateBoxes.value) {
            onFormFieldChange({
                productId,
                fieldPID: totalWidthPID,
                value: '',
            });
        }
    });
}

function formatCombinationTypeField({ value }: { value: string }) {
    const combinationTypePID = FilteredCombinationsOptions.value.COMBINATION_TYPE_PID.toString();

    if (!value) {
        value = 'Avtomatsko'; // Fallback value if value is not determined
    }

    productsSelectedForCombination.value.forEach((productId) => {
        onFormFieldChange({
            productId,
            fieldPID: combinationTypePID,
            value,
        });
    });
}

function formatNumberOfCombinationsField() {
    const numberOfCombinationsPID = FilteredCombinationsOptions.value.NUMBER_OF_COMBINATIONS_PID.toString();
    const numberOfCombinations = String(productsSelectedForCombination.value.length);

    productsSelectedForCombination.value.forEach((productId) => {
        onFormFieldChange({
            productId,
            fieldPID: numberOfCombinationsPID,
            value: numberOfCombinations,
        });
    });
}

function formatDataForMultiposition() {
    formatTotalWidthFields({ value: totalWidth.value });
    formatNumberOfCombinationsField();
    formatRollerShutterBoxSizeField();
    formatCombinedSystemBoxField();

    // Skip guide fields and combination type for simple configurator
    if (activeSteps.value.length < 4) {
        return;
    }

    formatGuidesForHeroProducts();

    if (!showCombinationType.value) return;

    formatCombinationTypeField({ value: combinationType.value });
}

function updateMultiposition() {
    productsSelectedForCombination.value.forEach(async (rowId: number) => {
        const fields = productRowsFormData.value.find((row) => row.activeProductFormId === rowId)?.formData;

        if (!fields) return;

        fields.forEach(async (field: any) => {
            if (!field.shouldUpdateConfigurator) return;
            let timeout = 50;

            setTimeout(() => {
                updateActiveProductFormValue({
                    pId: field.fieldPID,
                    value: field.fieldValue,
                    productFormId: rowId,
                });
            }, timeout);
        });
    });
}

function addCombination() {
    // Only allow adding combinations if the final step is completed
    if (!stepCompletions.value[stepCompletions.value.length - 1]) {
        console.warn(proxy!.$t('Please complete all required fields before adding the combination'));
        return;
    }

    formatDataForMultiposition();
    updateMultiposition();
    const combinationImageBase64: string | null =
        combinationsConfiguratorTemplateRef.value?.captureAsPngBase64?.() ?? null;
    const selectedRows = productData.value.filter((row) =>
        productsSelectedForCombination.value.includes(row.activeProductFormId),
    );

    nextTick(() => {
        EventBus.$emit(
            EventBusEvents.addNewCombinationFromConfigurator,
            selectedRows,
            shouldAddCombinationInMultiposition.value,
            combinationImageBase64,
            productName.value,
            'sestav',
        );
        closePopup();
    });
}

// ============================================================================
// User Action Handlers — Guide / Width
// ============================================================================
function updateVodilaField({ value, vodilaNumber }: { value: string; vodilaNumber: number }) {
    switch (vodilaNumber) {
        case 1:
            guideType.value = value;

            break;
        case 2:
            secondGuideType.value = value;

            break;
        default:
            break;
    }
}

function onGuideImageClick(payload: GuideImageClickPayload): void {
    activeGuideSelection.value = payload;
    console.log(payload);
}

function onGuideTypeSelect(type: GuideType): void {
    const selection = activeGuideSelection.value;
    if (!selection) return;

    setIndividualGuideType(selection.windowIndex, selection.corner, type);
}

function closeGuideTypeSelector(): void {
    activeGuideSelection.value = null;
}

function updateTotalWidth(value: number): void {
    if (typeof value !== 'number' || isNaN(value) || value < 0) {
        value = 0;
    }

    totalWidth.value = value;
}

function updateUseSeparateBoxes(value: boolean): void {
    useSeparateBoxes.value = value;
}

// ============================================================================
// Component Lifecycle — EventBus subscriptions
// ============================================================================
onMounted(() => {
    EventBus.$on(EventBusEvents.openCombinationsConfiguratorSelectorPopup, openPopup);
    EventBus.$on(EventBusEvents.closeContactPopup, closePopup);
    updateArrowVisibility();
});

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.openCombinationsConfiguratorSelectorPopup, openPopup);
    EventBus.$off(EventBusEvents.closeContactPopup, closePopup);
});

return { __sfc: true,proxy, getProductFormValues, updateActiveProductFormValue, visible, productData, productName, productRowsFormData, productsSelectedForCombination, totalWidth, useSeparateBoxes, guideType, secondGuideType, combinationsConfiguratorTemplateRef, tableHeaderFields, FilteredCombinationsOptions, currentConfig, checklistFieldConfig, activeSteps, shouldAddCombinationInMultiposition, shouldShowAssemblyCode, showCombinationType, canvasTemplateConfiguration, noChoiceGuideValue, getGuideTypeChecklistValue, configuratorType, setCorrectPIdsForFormData, resetConfig, sortedProductsSelectedForCombination, isFirstGuideSingle, isSecondGuideSingle, areBothGuidesSingle, isFirstGuidePartition, isSecondGuidePartition, areBothGuidesPartition, individualGuideTypes, guideTypeOptions, setIndividualGuideType, activeGuideSelection, activeGuideType, getWindowDriveValue, isSinglePartitionCombination, isPartitionSingleCombination, combinationType, isInvalidCombination, combinationsWarningMessage, activeCombinationsWarningMessage, combinedWidth, widthDifference, productFormValues, filteredProductRowsFormData, getTableFields, getDropdownTypeFields, dropdownChecklistFields, areFirstTwoWindowsOfSameHeight, areLastTwoWindowsOfSameHeight, getGuideOptionsWithMostValues, pogonOptions, secondPogonOptions, adjustOptionsForDropdowns, toggleProductSelection, currentStep, displayNextArrow, displayPrevArrow, isLastStep, currentStepDescription, stepCompletions, isStepCompletedByIndex, generateStepsAccessibilityArray, goToStep, goToNextStep, goToPrevStep, updateArrowVisibility, resetNavigation, stepInfoDescription, firstGuideComputedValue, secondGuideComputedValue, firstWindowRightGuideValue, secondWindowLeftGuideValue, secondWindowRightGuideValue, thirdWindowLeftGuideValue, renderFormData, onFormFieldChange, openPopup, resetForm, closePopup, formatTotalWidthFields, formatGuideFields, formatGuideFieldsWithGuideTypes, setShouldUpdateConfigurator, formatGuidesForHeroProducts, formatRollerShutterBoxSizeBasedOnSelectedSize, formatRollerShutterBoxSizeBasedOnDepthAndHeight, formatRollerShutterBoxSizeField, formatCombinedSystemBoxField, formatCombinationTypeField, formatNumberOfCombinationsField, formatDataForMultiposition, updateMultiposition, addCombination, updateVodilaField, onGuideImageClick, onGuideTypeSelect, closeGuideTypeSelector, updateTotalWidth, updateUseSeparateBoxes, CombinationsSelectorProgressBar, CombinationsConfiguratorTemplate, CombinationsProductsControls, CombinationsProductTable, CombinationsProductDropdowns, CombinationsTotalWidth, CombinationsWarning, CombinationsStepInfo, CombinationsSeparatedBoxes, GuideTypeSelectorModal }
}

})